// Prominent hero stat card showing the grand total for a selected period.
import { Card, Text, Caption1, Badge, makeStyles, tokens } from '@fluentui/react-components';
import type { TokenUsageDocument } from '../../types/tokenusage';
import { formatCompact, formatCount, formatPeriodLabel, isZeroUsage } from './formatUsage';

const useStyles = makeStyles({
  card: {
    padding: tokens.spacingVerticalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  totalRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  totalTokens: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightBold,
    lineHeight: tokens.lineHeightHero800,
  },
  subStat: { color: tokens.colorNeutralForeground3 },
  caption: { color: tokens.colorNeutralForeground3 },
});

export interface UsageHeroCardProps {
  document: TokenUsageDocument | undefined;
  /** Label override, e.g. "My usage" vs. a specific user's display name. */
  title?: string;
}

export function UsageHeroCard(props: UsageHeroCardProps) {
  const { document, title = 'Total usage' } = props;
  const styles = useStyles();

  if (!document) {
    return null;
  }

  const zero = isZeroUsage(document.grandTotal);
  const viaIdentityTotal = document.bySource?.viaIdentity?.grandTotal;
  const hasIdentityUsage = viaIdentityTotal != null && !isZeroUsage(viaIdentityTotal);

  return (
    <Card className={styles.card}>
      <div className={styles.headerRow}>
        <Text weight="semibold" size={400}>{title}</Text>
        <Caption1 className={styles.caption}>{formatPeriodLabel(document.period)}</Caption1>
        {!document.isFinal && (
          <Badge appearance="tint" color="brand" size="small">Still updating</Badge>
        )}
        {document.isFinal && (
          <Badge appearance="tint" color="informative" size="small">Archived</Badge>
        )}
      </div>

      {zero ? (
        <Text>No usage recorded yet for this period.</Text>
      ) : (
        <>
          <div className={styles.totalRow}>
            <span className={styles.totalTokens} title={formatCount(document.grandTotal.totalTokens)}>
              {formatCompact(document.grandTotal.totalTokens)}
            </span>
            <Text className={styles.subStat}>tokens</Text>
          </div>
          <Caption1 className={styles.caption}>
            {formatCount(document.grandTotal.requests)} requests
          </Caption1>
          {hasIdentityUsage && (
            <Caption1 className={styles.caption}>
              includes {formatCompact(viaIdentityTotal!.totalTokens)} tokens via managed identities
            </Caption1>
          )}
        </>
      )}
    </Card>
  );
}

export default UsageHeroCard;
